import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Plus, Pencil, Check, X, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, authJsonFetch } from '../lib/api';
import { cn } from '../lib/utils';
import { invalidateCFCardsCache } from './organization/CFCardField';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * Resource Library → CF Cards modal.
 *
 * Mirrors PhotographersModal: list / create / edit / delete with admin-only
 * mutation. Each card has a unique numeric ``card_number`` and an optional
 * free-text ``label`` (e.g. for marking a card as "spare" or "physically
 * damaged" in plain English).
 */
export const CFCardsModal = ({ open, onClose, isAdmin = false, isBarbiePink = false }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ card_number: '', label: '' });
  const [creatingRow, setCreatingRow] = useState(false);
  const [createForm, setCreateForm] = useState({ card_number: '', label: '' });
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/cf-cards`);
      if (!res.ok) {
        toast.error('Failed to load CF cards');
        return;
      }
      const data = await res.json();
      setItems(data.data || []);
    } catch (_e) {
      toast.error('Failed to load CF cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startEdit = (row) => {
    setEditingId(row.card_id);
    setEditForm({
      card_number: String(row.card_number ?? ''),
      label: row.label || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ card_number: '', label: '' });
  };

  const saveEdit = async (id) => {
    const num = parseInt(editForm.card_number, 10);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Card # must be a positive number');
      return;
    }
    setSavingId(id);
    try {
      const res = await authJsonFetch(`${API}/cf-cards/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ card_number: num, label: editForm.label }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return;
      }
      const updated = await res.json();
      setItems((prev) =>
        prev
          .map((p) => (p.card_id === id ? updated : p))
          .sort((a, b) => (a.card_number || 0) - (b.card_number || 0))
      );
      invalidateCFCardsCache();
      cancelEdit();
      toast.success('CF card updated');
    } catch (_e) {
      toast.error('Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete CF #${row.card_number}? This cannot be undone.`)) return;
    setSavingId(row.card_id);
    try {
      const res = await authFetch(`${API}/cf-cards/${row.card_id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Delete failed');
        return;
      }
      setItems((prev) => prev.filter((p) => p.card_id !== row.card_id));
      invalidateCFCardsCache();
      toast.success(`Deleted CF #${row.card_number}`);
    } catch (_e) {
      toast.error('Delete failed');
    } finally {
      setSavingId(null);
    }
  };

  const submitCreate = async () => {
    const num = parseInt(createForm.card_number, 10);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Card # must be a positive number');
      return;
    }
    setSavingId('__new__');
    try {
      const res = await authJsonFetch(`${API}/cf-cards`, {
        method: 'POST',
        body: JSON.stringify({ card_number: num, label: createForm.label }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Create failed');
        return;
      }
      const created = await res.json();
      setItems((prev) => [...prev, created].sort((a, b) => (a.card_number || 0) - (b.card_number || 0)));
      invalidateCFCardsCache();
      setCreatingRow(false);
      setCreateForm({ card_number: '', label: '' });
      toast.success(`Added CF #${created.card_number}`);
    } catch (_e) {
      toast.error('Create failed');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cf-cards-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            CF Cards
          </DialogTitle>
          <DialogDescription>
            Compact-Flash card inventory. {isAdmin ? 'Click any row to edit; use the + button to add a new card.' : 'Read-only — contact an admin to make changes.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead className="w-[20%]">Card #</TableHead>
                  <TableHead className="w-[60%]">Label / Notes</TableHead>
                  {isAdmin && <TableHead className="w-[20%] text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && !creatingRow ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 3 : 2} className="text-center py-8 text-muted-foreground">
                      No CF cards yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => {
                    const editing = editingId === row.card_id;
                    return (
                      <TableRow key={row.card_id} data-testid={`cf-card-row-${row.card_id}`}>
                        <TableCell>
                          {editing ? (
                            <Input
                              type="number"
                              min={0}
                              value={editForm.card_number}
                              onChange={(e) => setEditForm((f) => ({ ...f, card_number: e.target.value }))}
                              data-testid={`cf-card-edit-number-${row.card_id}`}
                            />
                          ) : (
                            <span className="font-mono font-semibold">CF {row.card_number}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editing ? (
                            <Input
                              value={editForm.label}
                              onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
                              placeholder="(optional notes)"
                              data-testid={`cf-card-edit-label-${row.card_id}`}
                            />
                          ) : (
                            <span className={cn(row.label ? '' : 'text-muted-foreground')}>
                              {row.label || '—'}
                            </span>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {editing ? (
                              <div className="flex items-center gap-1 justify-end">
                                <Button size="icon" variant="ghost" onClick={() => saveEdit(row.card_id)} disabled={savingId === row.card_id} data-testid={`cf-card-save-${row.card_id}`}>
                                  {savingId === row.card_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                </Button>
                                <Button size="icon" variant="ghost" onClick={cancelEdit} data-testid={`cf-card-cancel-${row.card_id}`}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 justify-end">
                                <Button size="icon" variant="ghost" onClick={() => startEdit(row)} data-testid={`cf-card-edit-${row.card_id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDelete(row)}
                                  className="text-destructive hover:text-destructive"
                                  disabled={savingId === row.card_id}
                                  data-testid={`cf-card-delete-${row.card_id}`}
                                >
                                  {savingId === row.card_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}

                {creatingRow && (
                  <TableRow data-testid="cf-card-new-row">
                    <TableCell>
                      <Input
                        autoFocus
                        type="number"
                        min={0}
                        placeholder="e.g. 221"
                        value={createForm.card_number}
                        onChange={(e) => setCreateForm((f) => ({ ...f, card_number: e.target.value }))}
                        data-testid="cf-card-new-number"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Label (optional)"
                        value={createForm.label}
                        onChange={(e) => setCreateForm((f) => ({ ...f, label: e.target.value }))}
                        data-testid="cf-card-new-label"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={submitCreate} disabled={savingId === '__new__'} data-testid="cf-card-new-save">
                          {savingId === '__new__' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => { setCreatingRow(false); setCreateForm({ card_number: '', label: '' }); }}
                          data-testid="cf-card-new-cancel"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-muted-foreground">{items.length} card{items.length === 1 ? '' : 's'}</p>
            <div className="flex items-center gap-2">
              {isAdmin && !creatingRow && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCreatingRow(true)}
                  className={cn(isBarbiePink && 'border-pink-500')}
                  data-testid="cf-card-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="cf-cards-close-btn">
                Close
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CFCardsModal;
